import { NgClass } from '@angular/common';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { EditBillingInfoComponent } from 'components/company/billing-info/edit-billing-info.component';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { ListContactComponent } from 'components/company/contact/list-contact.component';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { ListViewComponent } from 'components/layout/list-view/list-view.component';
import { BcDocumentType } from 'enums/BcDocumentType';
import { CredentialType } from 'enums/CredentialType';
import { MessageType } from 'enums/MessageType';
import { IssuedReminderModel } from 'models/generated/BusinessCentral.Integration.Models.IssuedReminder';
import { InvoiceObjectionModel } from 'models/generated/Entities.Accounting.InvoiceObjection';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { BillingInfoDtoModel } from 'models/generated/Models.BillingInfoDto';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { InvoicingService } from 'services/invoicing.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DataTracking } from 'services/data-tracking';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { LedgerEntryDetailsComponent } from './ledger-entry-details.component';
import { InstallmentPaymentModel } from 'models/generated/Entities.Accounting.InstallmentPayment';
import { DateService } from 'services/date.service';
import { BrandService } from 'services/brand.service';
import { Pipes } from 'modules/pipes';
import { LedgerHeaderModel } from 'models/generated/Entities.Accounting.LedgerHeader';

export interface LedgerEntryDisplayModel {
	brandId?: string;
	documentNo: string;
	documentId?: string;
	documentType: BcDocumentType;
	postingDate?: Date;
	dueDate?: Date;
	amount?: number;
	amountInclVat?: number;
	open?: boolean;
	customerNo: string;
	entryNo?: number;
	remainingAmountInclVat?: number;
	description?: string;
	ledgerHeader?: LedgerHeaderModel;
}

@Component({
	selector: 'company-invoices',
	styleUrls: ['./company-invoices.component.scss'],
	templateUrl: './company-invoices.component.html',
	providers: [CompanyDataProviderFactory, DataTracking],
	imports: [Layout, DateInputComponent, ListView, NgClass, LedgerEntryDetailsComponent, CompanyInformationComponent, ListContactComponent, Pipes]
})
@ComponentUrl('company-invoices')
export class CompanyInvoicesComponent implements OnDestroy {
	@ViewChild('entryDetails') entryDetails?: LedgerEntryDetailsComponent;
	@ViewChild('entriesList') entriesList?: ListViewComponent;

	private dataProvider?: CompanyDataProvider;
	private invoiceChangeSubscription: Subscription;
	private installmentPaymentChangeSubscription: Subscription;
	private _companyId = '';
	private goBrand: string;
	private wkBrand: string;

	public today = new Date();
	public credentialType = CredentialType;
	public bcDocumentType = BcDocumentType;
	public billingInfo?: BillingInfoDtoModel;
	public selectedTaskflowId?: string;
	public ledgerEntries: LedgerEntryDisplayModel[] = [];
	public filteredLedgerEntries: LedgerEntryDisplayModel[] = [];
	public company?: CompanyModel;
	public loading = true;
	public error = false;
	public printMode = false;
	public fromDate?: Date;
	public toDate?: Date;
	public oldestDate?: Date;
	public newestDate?: Date;

	public totalLedgerEntryValues = 0;
	public totalFilteredLedgerEntryValuesInclVat = 0;
	public previousTotalLedgerEntryValues?: number;

	private _selectedCustomerLedgerEntry?: LedgerEntryDisplayModel;
	set selectedCustomerLedgerEntry(entry: LedgerEntryDisplayModel | undefined) {
		this._selectedCustomerLedgerEntry = entry;
		this.selectDocumentNo = entry?.documentNo;
		this.selectBrandId = entry?.brandId;
		this.navigation.refreshUrl();
	}
	get selectedCustomerLedgerEntry() {
		return this._selectedCustomerLedgerEntry;
	}
	invoiceIds = new Map<string, string>();
	creditMemoIds = new Map<string, string>();
	private issuedRemindersMap = new Map<string, IssuedReminderModel>();
	private farpayInvoiceIds = new Map<string, Map<string, number>>();
	private invoiceObjectionsMap = new Map<string, Map<string, InvoiceObjectionModel>>();
	private installmentPaymentsMap = new Map<string, Map<string, InstallmentPaymentModel[]>>();
	private showPaymentsListMap = new Map<string, Map<string, boolean>>();

	@UrlParam
	set companyId(id: string) {
		if (id) {
			this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);
			this._companyId = id;
			this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent)
				.then(viewCompany => { viewCompany.companyId = id });
			this.loadLedgerEntries();
			this.getCompany();
		}
	}
	get companyId() {
		return this._companyId;
	}

	@UrlParam selectBrandId?: string;
	@UrlParam selectDocumentNo?: string;

	constructor(
		private readonly dataProviderFactory: CompanyDataProviderFactory,
		private readonly invoicingService: InvoicingService,
		private readonly navigation: NavigationService,
		private readonly pushService: PushService,
		private readonly dateService: DateService,
		private readonly translate: TranslatePipe,
		private readonly accountingService: AccountingService,
		private readonly brandService: BrandService,
	) {

		this.goBrand = this.brandService.brands.find(b => b.shortName === 'GO')?.id!;
		this.wkBrand = this.brandService.brands.find(b => b.shortName === 'WK')?.id!;

		for (const brand of this.brandService.brands) {
			this.installmentPaymentsMap.set(brand.id!, new Map<string, InstallmentPaymentModel[]>());
			this.showPaymentsListMap.set(brand.id!, new Map<string, boolean>());
			this.invoiceObjectionsMap.set(brand.id!, new Map<string, InvoiceObjectionModel>());
		}

		this.invoiceChangeSubscription = this.pushService.onChanges<{ companyId: string, entryNo?: number, documentNo?: string, brandId?: string }>(MessageType.InvoiceChanges).subscribe(async message => {
			if (!message.data) return;

			if (message.data.companyId && message.data.companyId === this.companyId) await this.loadInvoiceObjections();

			const ledgerEntryToUpdate = this.ledgerEntries.find(l =>
				(message.data.entryNo && l.entryNo == message.data.entryNo && (!message.data.brandId || l.brandId == message.data.brandId))
				|| (message.data.documentNo && l.documentNo == message.data.documentNo) && (!message.data.brandId || l.brandId == message.data.brandId));

			if (ledgerEntryToUpdate) await this.refreshLedgerEntry(ledgerEntryToUpdate);
		});

		this.installmentPaymentChangeSubscription = this.pushService.onChanges<{ documentNo: string, brandId: string }>(MessageType.InstallmentPaymentChanges).subscribe(message => {
			if (this.ledgerEntries.find(e => e.documentNo == message.data.documentNo && e.brandId == message.data.brandId))
				this.loadInstallmentPayments();
		});
	}

	isEntrySelected(brandId: string, entryNo: number | undefined, documentNo: string) {
		if (!this.selectedCustomerLedgerEntry) return false;
		return (this.selectedCustomerLedgerEntry.brandId == brandId && (this.selectedCustomerLedgerEntry.entryNo == entryNo || this.selectedCustomerLedgerEntry.documentNo == documentNo));
	}
	showPaymentsList(ledgerEntry: LedgerEntryDisplayModel, setValue?: boolean) {
		if (!ledgerEntry.brandId) return false;
		if (setValue !== undefined) this.showPaymentsListMap.get(ledgerEntry.brandId)?.set(ledgerEntry.documentNo, setValue);
		else return this.showPaymentsListMap.get(ledgerEntry.brandId)?.get(ledgerEntry.documentNo) ?? true;
	}
	installmentPayments(ledgerEntry: LedgerEntryDisplayModel) {
		if (!ledgerEntry.brandId) return undefined;
		return this.installmentPaymentsMap.get(ledgerEntry.brandId)?.get(ledgerEntry.documentNo);
	}
	invoiceObjection(ledgerEntry: LedgerEntryDisplayModel) {
		if (!ledgerEntry.brandId) return undefined;
		return this.invoiceObjectionsMap.get(ledgerEntry.brandId)?.get(ledgerEntry.documentNo);
	}
	issuedReminder(ledgerEntry: LedgerEntryDisplayModel) {
		if (ledgerEntry.brandId != this.goBrand) return undefined;
		return this.issuedRemindersMap.get(ledgerEntry.documentNo);
	}

	getCompany() {
		if (!this.dataProvider) return;
		this.dataProvider.getCompany().then(company => {
			this.company = company;
		})

		this.dataProvider.getBillingInfo().then(info => {
			this.billingInfo = info;
		});
	}

	getDocumentId(ledgerEntry?: LedgerEntryDisplayModel) {
		if (!ledgerEntry) return;

		if (ledgerEntry.documentId) return ledgerEntry.documentId;
		if (ledgerEntry.documentType === BcDocumentType.Invoice && ledgerEntry.brandId == this.goBrand) {
			return this.invoiceIds.get(ledgerEntry.documentNo);
		}
		if (ledgerEntry.documentType === BcDocumentType.CreditMemo && ledgerEntry.brandId == this.goBrand) {
			return this.creditMemoIds.get(ledgerEntry.documentNo);
		}
		return;
	}

	getFarpayInvoiceId(ledgerEntry?: LedgerEntryDisplayModel) {
		if (!ledgerEntry || !ledgerEntry.brandId || ledgerEntry.documentType !== BcDocumentType.Invoice) return undefined;
		return this.farpayInvoiceIds.get(ledgerEntry.brandId)?.get(ledgerEntry.documentNo);
	}

	togglePrintMode() {
		this.printMode = !this.printMode;
		this.entriesList?.resetFilters();
	}

	async getLedgerEntriesForCompany(): Promise<LedgerEntryDisplayModel[]> {

		const ledgerEntryGroups = await Promise.all([
			// TODO (#23771): Single endpoint with common interface and brandId property. Should probably also include the salesInvoiceIds and creditMemoIds in the same request (since documentId and brandId is a part of the interface - saves using the lookup objects)
			this.invoicingService.getBusinessCentralLedgerEntriesByCompanyId(this.companyId, this.goBrand)
				.then((entries: LedgerEntryDisplayModel[]) => {
					entries.forEach(e => e.brandId = this.goBrand);
					return entries;
				}).then(entries => this.getLedgerHeaders(entries, this.goBrand)),
			this.invoicingService.getEconomicLedgerEntriesByCompanyId(this.companyId, this.wkBrand).then(entries =>
				entries.map(e => {
					return {
						brandId: this.wkBrand,
						documentNo: e.type == 1 ? (e.customerInvoiceNumber?.toString() ?? '') : e.type == 2 ? e.entryNumber?.toString() : '',
						documentId: e.type == 1 ? e.customerInvoiceNumber?.toString() : undefined, // Allows downloading invoice as PDF
						documentType: e.type == 1 ? (e.amount >= 0 ? BcDocumentType.Invoice : BcDocumentType.CreditMemo) : e.type == 2 ? BcDocumentType.Payment : BcDocumentType.Unknown,
						postingDate: e.date,
						dueDate: e.dueDate,
						amount: e.amountExclVat,
						amountInclVat: e.amountInclVat,
						open: e.remainder != 0,
						customerNo: e.customerNumber!.toString(),
						entryNo: e.entryNumber,
						remainingAmountInclVat: e.remainder,
						description: e.text
					} as LedgerEntryDisplayModel;
				})).then(entries => this.getLedgerHeaders(entries, this.wkBrand)),
		]);

		return ledgerEntryGroups.flat();
	}

	async getLedgerHeaders(entries: LedgerEntryDisplayModel[], brandId: string) {
		const postedDocuments = entries.filter(e => e.documentType == BcDocumentType.Invoice || e.documentType == BcDocumentType.CreditMemo);
		const ledgerPosts = await this.accountingService.getLedgerPostsByPostedDocumentNumbers(postedDocuments.map(e => e.documentNo), brandId);
		const ledgerHeaders = ledgerPosts.map(p => p.ledgerHeader);
		for (const entry of entries)
			entry.ledgerHeader = ledgerHeaders.find(h => h?.postedDocumentNo == entry.documentNo && h?.brandId == brandId);

		return entries;
	}

	async refreshLedgerEntry(entryToUpdate: LedgerEntryDisplayModel) {
		const ledgerEntries = await this.getLedgerEntriesForCompany();
		const updatedEntry = ledgerEntries.find(e => e.brandId == entryToUpdate.brandId && e.documentNo == entryToUpdate.documentNo);
		if (!updatedEntry) return;

		Object.assign(entryToUpdate, updatedEntry);
		if (this.isEntrySelected(entryToUpdate.brandId!, entryToUpdate.entryNo, entryToUpdate.documentNo)) this.entryDetails?.reloadEntry();
		this.filterLedgerEntries();
	}

	async loadLedgerEntries() {
		this.loading = true;
		this.invoicingService.getSalesInvoiceIdsByCompanyId(this.companyId, this.goBrand).then((invoiceIds: Record<string, string>) => {
			this.invoiceIds = new Map(Object.entries(invoiceIds).map(([k, v]) => [k, v]));
		});
		this.invoicingService.getCreditMemoIdsByCompanyId(this.companyId, this.goBrand).then((creditMemos: Record<string, string>) => {
			this.creditMemoIds = new Map(Object.entries(creditMemos).map(([k, v]) => [k, v]));
		});
		Promise.all([
			this.accountingService.getFarPayInvoiceIdsByCompanyId(this.companyId, this.goBrand),
			this.accountingService.getFarPayInvoiceIdsByCompanyId(this.companyId, this.wkBrand)
		]).then(([goInvoiceIds, wkInvoiceIds]) => {
			this.farpayInvoiceIds = new Map([
				[this.goBrand, new Map(Object.entries(goInvoiceIds).map(([k, v]) => [k, v]))],
				[this.wkBrand, new Map(Object.entries(wkInvoiceIds).map(([k, v]) => [k, v]))]
			]);
		});
		try {
			this.ledgerEntries = await this.getLedgerEntriesForCompany();
		}
		catch (error) {
			this.ledgerEntries = [];
			this.filteredLedgerEntries = [];
			this.error = true;
			this.loading = false;
			return;
		}

		this.loadInstallmentPayments();
		if (this.selectDocumentNo && this.selectBrandId) {
			const matchingLedgerEntry = this.ledgerEntries.find(e => e.documentNo === this.selectDocumentNo && e.brandId == this.selectBrandId);
			if (matchingLedgerEntry) this.selectedCustomerLedgerEntry = matchingLedgerEntry; // Only set ledger entry if found, to avoid navigating to a new URL even when a match didn't exist
		}

		if (!this.ledgerEntries?.length) this.totalLedgerEntryValues = 0;
		else this.totalLedgerEntryValues = this.ledgerEntries.map(entry => entry.amount!).reduce((partialSum, a) => partialSum + a, 0);

		this.ledgerEntries.sort((a, b) => a.postingDate && b.postingDate ? a.postingDate.getTime() - b.postingDate.getTime() : 0);
		this.oldestDate = this.ledgerEntries.length > 0 ? this.ledgerEntries[0].postingDate : undefined;
		this.newestDate = this.ledgerEntries.length > 0 ? this.ledgerEntries[this.ledgerEntries.length - 1].postingDate : undefined;

		this.filterLedgerEntries();
		await this.loadInvoiceObjections();
		this.loading = false;
	}

	private async loadInstallmentPayments() {
		const invoices = this.ledgerEntries.filter(l => l.documentType == BcDocumentType.Invoice);
		const payments = await this.accountingService.getInstallmentPayments(invoices);
		for (const invoice of invoices) {
			const documentPayments = payments.filter(p => p.documentNo == invoice.documentNo && p.brandId == invoice.brandId);
			if (documentPayments.length > 0) this.installmentPaymentsMap.get(invoice.brandId!)?.set(invoice.documentNo, documentPayments);
			else this.installmentPaymentsMap.get(invoice.brandId!)?.delete(invoice.documentNo);
		}
	}

	filterLedgerEntries() {
		// sort by posting date then entryno
		this.ledgerEntries.sort((a, b) => {
			if (a.postingDate && b.postingDate) {
				if (a.postingDate.getTime() === b.postingDate.getTime()) {
					return a.entryNo! - b.entryNo!;
				}
				return a.postingDate.getTime() - b.postingDate.getTime();
			}
			return 0;
		});

		this.filteredLedgerEntries = this.ledgerEntries.filter(entry => {
			if (this.fromDate && entry.postingDate && entry.postingDate < this.fromDate) return false;
			if (this.toDate && entry.postingDate && entry.postingDate > this.toDate) return false;
			return true;
		});
		const previousLedgerEntries = this.ledgerEntries.filter(entry => {
			if (this.fromDate && entry.postingDate && entry.postingDate < this.fromDate) return true;
			return false;
		});
		if (previousLedgerEntries.length == 0) this.previousTotalLedgerEntryValues = undefined;
		else this.previousTotalLedgerEntryValues = previousLedgerEntries.map(entry => entry.amountInclVat!).reduce((partialSum, a) => partialSum + a, 0);

		this.totalFilteredLedgerEntryValuesInclVat = this.filteredLedgerEntries.map(entry => entry.amountInclVat!).reduce((partialSum, a) => partialSum + a, 0) + (this.previousTotalLedgerEntryValues ?? 0);
	}

	getCumulativeTotal(ledgerEntry: LedgerEntryDisplayModel): number {
		let total = this.previousTotalLedgerEntryValues ?? 0;
		if (!this.filteredLedgerEntries.length) return total;
		let entriesToSum = this.filteredLedgerEntries.filter(entry => entry.postingDate! < ledgerEntry.postingDate! || (entry.postingDate!.getTime() == ledgerEntry.postingDate!.getTime() && entry.entryNo! <= ledgerEntry.entryNo!));
		
		return total + entriesToSum.map(entry => entry.amountInclVat!).reduce((partialSum, a) => partialSum + a, 0);
	}

	async loadInvoiceObjections() {
		if (this.ledgerEntries.length == 0) return;
		var goBrandCustomerNos = this.ledgerEntries.filter(le => le.brandId == this.goBrand).map(le => le.customerNo).filter((value, index, array) => { return array.indexOf(value) === index; })
		await Promise.all([
			this.invoicingService.getInvoiceObjections(this.ledgerEntries).then(objections => {
				for (const key in this.invoiceObjectionsMap.keys) this.invoiceObjectionsMap.set(key, new Map<string, InvoiceObjectionModel>());
				for (const objection of objections) this.invoiceObjectionsMap.get(objection.brandId!)?.set(objection.documentNo, objection);
			}),
			this.invoicingService.getIssuedReminders(goBrandCustomerNos).then(reminders => {
				this.issuedRemindersMap = new Map<string, IssuedReminderModel>(reminders.map(reminder => [reminder.documentNo, reminder]));
			})
		]);
	}

	detailedViewAvailable(ledgerEntry: LedgerEntryDisplayModel): boolean {
		return ledgerEntry.documentType === BcDocumentType.Invoice || ledgerEntry.documentType === BcDocumentType.CreditMemo
	}

	entryStatusColor(ledgerEntry: LedgerEntryDisplayModel): string {

		if (this.invoiceObjection(ledgerEntry) || this.requiresAttention(ledgerEntry)) 
			return 'color-red-50';

		switch (ledgerEntry.documentType) {
			case BcDocumentType.Payment:
				return 'color-green-50';
			case BcDocumentType.CreditMemo:
				return 'color-red-50';
			default:
				return 'colorGray';
		}
	}
	paymentStatusColor(payment: InstallmentPaymentModel) {

		if (payment.paid) return 'color-green-50';
		if (this.dateService.getDate(payment.dueDate!) < this.dateService.today()) return 'color-red-50';
		return 'colorGray';
	}
	entryStatusTitle(ledgerEntry: LedgerEntryDisplayModel): string {

		if (this.invoiceObjection(ledgerEntry)) return 'objection';
		if (ledgerEntry.open && this.issuedReminder(ledgerEntry)) return 'reminder';
		if (ledgerEntry.open && ledgerEntry.ledgerHeader?.onHoldReason) return 'on-hold';

		switch (ledgerEntry.documentType) {
			case BcDocumentType.Payment:
				return 'payment';
			case BcDocumentType.CreditMemo:
				return 'credit';
			case BcDocumentType.Invoice:
				return 'invoice';
			default:
				return 'other';
		}
	}

	editBillingInfo() {
		this.navigation.openPopup(EditBillingInfoComponent, 'company-invoices.edit-billing-info.popup-header').then(view => {
			view.company = this.company!;
		});
	}

	getLedgerRemarks(documentNo: string): string {
		var entry = this.ledgerEntries.find(l => l.documentNo == documentNo);
		if (!entry) return '';

		var issuedReminder = this.issuedReminder(entry);
		
		if (issuedReminder && entry.ledgerHeader?.onHoldReason) return this.translate.transform('company-invoices.ledger-remark.reminder-and-on-hold', { 'reminderLevel': issuedReminder.reminderLevel });
		else if (issuedReminder) return  this.translate.transform('company-invoices.ledger-remark.reminder', { 'reminderLevel': issuedReminder.reminderLevel });
		else if (entry.ledgerHeader?.onHoldReason) return this.translate.transform('company-invoices.ledger-remark.on-hold');
		else return '';
	}

	requiresAttention(ledgerEntry: LedgerEntryDisplayModel): boolean {
		const issuedReminder = this.issuedReminder(ledgerEntry);
		return !!ledgerEntry.open && (!!issuedReminder || !!ledgerEntry.ledgerHeader?.onHoldReason );
	}

	print() {
		print();
	}

	ngOnDestroy() {
		this.invoiceChangeSubscription.unsubscribe();
		this.installmentPaymentChangeSubscription.unsubscribe();
	}
}
